/**
 * Responsibility: describe the badlands biome — soft rock dissected into a maze of small sharp ridges.
 *
 * ## Why this is its own biome and not part of mesa country
 *
 * The test that separated canyon from mesa applies again: does a feature need its own *base* elevation, or does it
 * only modify one? Mesa's base is a quantised staircase and canyon's is a raised rim cut down through. Badlands is
 * neither — it is ridged relief at high frequency — so it cannot be a tweak to either. Folded into mesa it would
 * have to fight the quantisation rather than replace it, and the result would be a rough mesa, which is not
 * badlands.
 *
 * Independence is also honest geography. The Dakota badlands are surrounded by prairie, not tableland, so badlands
 * bordering open ground is right rather than a compromise.
 *
 * ## Ridged noise: the canyon's trick, inverted
 *
 * `1 - |noise|` peaks along the field's zero-crossing contours, which are the same winding, branching curves that
 * give canyons their channels. So ridged noise lays *ridges* along that curve family, and three octaves superimpose
 * three scales of them. That is what a dissected landscape is, and it costs one field.
 *
 * ## What makes it read as badlands rather than as small mountains
 *
 * Not steepness but the *spread* of steepness. Its median slope is just under the mountains' while both its 90th
 * and 99th percentiles sit well below — almost as steep as a range on average, never as steep as a crest. Every
 * other biome is the opposite shape: mesa is level ground punctuated by cliffs. This is the only terrain in the
 * world that is steep nearly everywhere. See `BADLANDS` in config for the measurements.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createRidgedFbm } from '../noise.js';
import { createRockFormations } from '../rockFormations.js';
import { BADLANDS, PALETTE } from '../../config.js';

export const badlands = {
  id: 'badlands',
  label: 'Badlands',

  palette: PALETTE.badlands,

  suitabilityBias: BADLANDS.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const ridges = createRidgedFbm(seed, 'elevation:badlands', {
      octaves: BADLANDS.OCTAVES,
      baseFrequency: 1 / BADLANDS.WAVELENGTH,
      persistence: BADLANDS.PERSISTENCE,
      lacunarity: BADLANDS.LACUNARITY,
    });

    /** Width of the band being stretched, hoisted so it is not recomputed per sample. */
    const bandWidth = BADLANDS.FIELD_HIGH - BADLANDS.FIELD_LOW;

    return function sampleBadlandsElevation(x, z) {
      /*
        Ridged noise is already in 0..1, so unlike the plain-fBm biomes there is nothing to remap — and it can
        never contribute a negative elevation, which a weighted sum would otherwise drag neighbouring ground down
        through zero.
      */
      const raw = ridges(x, z);

      /*
        Stretch the band the field *actually* occupies across the full crest height.

        **fBm never approaches its own extremes, and this is the fourth time the project has been caught by it.**
        Measured, this field runs 0.10 to 0.85, so used raw the crests topped out at 178 units of a configured 210
        and the whole biome came out gentler than intended. Clamping outside the band costs nothing: beyond those
        percentiles the ground is already at a gully floor or on a crest.
      */
      const stretched = Math.min(1, Math.max(0, (raw - BADLANDS.FIELD_LOW) / bandWidth));

      return stretched * BADLANDS.CREST_HEIGHT;
    };
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: BADLANDS.CREST_HEIGHT,

  /**
   * Build the structure placement function, shared with mesa and canyon country.
   *
   * **Not a convenience — Bryce Canyon is badlands with hoodoos in it**, so this is the correct pairing rather
   * than a reused one. Measured, this biome grows about 3 columns a tile against mesa's 7: they collect on the
   * roughly quarter of the ground that is level, which is where they stand in the real thing.
   *
   * ## Arches all but never appear here, and that is right
   *
   * Measured at zero per tile across forty badlands tiles, against mesa's 0.23. An arch needs two feet a few
   * hundred units apart on ground within 45 units of level, and badlands puts 250 units of relief between crests
   * 240 units apart — so the arch's own ground rules exclude it with no special case anywhere. That matches the
   * real distribution: arches form in sandstone fins, not in badlands clay. A rule that produces the correct
   * geology as a side effect of its own physics is worth more than one written to say so.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    return createRockFormations(seed, 'badlands');
  },
};
